export interface CompleteApp {
  key: string;
  name: string;
  url: string;
  logoUrl: string;
  default: boolean;
}

export interface AppConfigV2 {
  name: string | null;
  url: string | null;
}

export interface AppBasic {
  uuid: string | null;
  name: string;
  key: string;
  default: boolean;
  config: AppConfigV2;
  url: string | null;
}

export interface AppDetailed {
  uuid: string | null;
  name: string;
  key: string;
  updatedAt: string | null;
  updatedBy: string | null;
  default: boolean;
  config: AppConfigV2;
  url: string | null;
  ssoEnabled: boolean;
  integrationEnabled: boolean;
}

export interface PropertyOption {
  options: Array<string>;
}

export interface PropertyDetail {
  uuid: string;
  value: string;
  inherited: boolean;
  key: string;
  displayName: string | null;
  enabled: boolean;
  keyGroup: string | null;
  valueType: string;
  valueOptions: PropertyOptions | null;
  isLocked: boolean;
  isExternalId: boolean;
  resourceKey?: ResourceKey;
  userPermissions?: Array<'CREATE' | 'READ' | 'UPDATE' | 'DELETE'>;
}

export type PropertyWithOrgUnit = PropertyDetail & {
  orgUnitName: string;
  orgUnitType: 'member' | 'location';
  locationKey?: string;
};

export enum BrandPropertyInheritType {
  BRAND_ONLY = 'brandOnly',
  DATAOWNERS = 'dataowners',
  LOCATIONS = 'locations',
  DATAOWNERS_AND_LOCATIONS = 'dataownersAndLocations',
}

export interface InheritableProperty {
  dataownerInheritable: boolean;
  locationInheritable: boolean;
  config: PropertyConfig;
}

export type BrandProperty = PropertyDetail &
  Pick<InheritableProperty, 'dataownerInheritable' | 'locationInheritable'>;

export interface TransformedInheritablePropertyValue {
  valueType: string;
  value: string;
  orgUnitType: InformationModelType;
  orgUnitName: string;
  orgUnitKey: string;
  parentOrgUnitKey?: string;
  parentOrgUnitName?: string;
}

export interface TransformedBrandProperty extends BrandProperty {
  hierarchy: Array<string>;
}
export interface TransformedPropertyValue extends TransformedInheritablePropertyValue {
  hierarchy: Array<string>;
}

export type ResourceKey =
  | 'VIEWABLE_PROPERTY'
  | 'MEMBER_PROPERTY'
  | 'ADMIN_PROPERTY'
  | 'LOCKED_PROPERTY';

export interface PropertyConfig {
  uuid: string;
  key: string;
  displayName: string | null;
  enabled: boolean;
  keyGroup: string | null;
  valueType: string;
  valueOptions: PropertyOptions;
  isLocked: boolean;
  isExternalId: boolean;
  resourceKey?: ResourceKey;
}

export type PropertyOptions = {
  options: Array<string>;
};

export enum PropertyValueTypes {
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  date = 'date',
  enum = 'enum',
  multipleChoice = 'multiple-choice',
  list = 'list',
  map = 'map',
}

export type CreateProperty = {
  value: string;
  inherited: boolean;
} & CreatePropertyConfig;

export interface CreateInheritableProperty {
  dataownerInheritable: boolean;
  locationInheritable: boolean;
  config: CreatePropertyConfig;
}

type CreatePropertyConfig = {
  key: string;
  displayName: string | null;
  enabled: boolean;
  keyGroup: string | null;
  valueType: string;
  valueOptions: PropertyOptions | null;
  isLocked: boolean;
  isExternalId?: boolean;
  resourceKey?: ResourceKey;
};

export interface EditProperty {
  uuid: string;
  value: string;
  resourceKey?: ResourceKey;
  valueOptions?: PropertyOptions | null;
}

export interface EditInheritableProperty {
  dataownerInheritable: boolean;
  locationInheritable: boolean;
  config: {
    displayName: string;
    keyGroup: string;
    isLocked: boolean;
    enabled: boolean;
    resourceKey?: ResourceKey;
    valueType?: string;
    valueOptions?: PropertyOptions | null;
    options?: Array<string>;
  };
}

/**
 * @deprecated replaced by @RegionV2
 */
export interface Region {
  key: string;
  id: number;
  name: string;
  desc: string;
  postFix: string;
}

export interface RegionV2 {
  uuid: string;
  name: string;
  key: string;
}

export enum AvailableLocationFields {
  PinMeTo = 'pinmeto',
  Erp = 'erp',
  Gln = 'gln',
  PubliclyAvailable = 'publiclyAvailable',
  Omnium = 'omnium',
  Ecommerce = 'ecommerce',
  MgPris = 'mgPris',
  ShowDataownerAndLocationUnited = 'showDataownerAndLocationUnited',
}

export interface EcomAssortment {
  key: string;
  title: string;
  selected: boolean;
}

export enum InformationModelType {
  Brand = 'brand',
  Dataowner = 'dataowner',
  Location = 'location',
  Super = 'super',
  Employee = 'employee',
}

export type LocationField =
  | 'gln'
  | 'publiclyAvailable'
  | 'ecommerce'
  | 'pinmeto'
  | 'mgPris'
  | 'erp';

export interface BrandLocationFieldConfig {
  fieldName: LocationField;
  show: boolean;
  id: number | null;
}

export interface Role {
  '@type': InformationModelType;
  key: string; //entityKey to match a brand, dataowner or location
  role: string;
  title: string;
  genericTitle?: string;
  selfAssign?: boolean;
  admin?: boolean;
}

export interface Roles {
  '@type': InformationModelType;
  key: string;
  roles: string[];
}

export interface RoleDetailed {
  key: string;
  title: string;
  isAdmin: boolean;
  selected: boolean;
}

export interface OrgUnitRole {
  role: RoleDetailed;
  orgUnitKey: string;
  type: string;
}

export enum ErpType {
  NEXSTEP = 'NEXSTEP',
  VISMA_BUSINESS = 'VISMA_BUSINESS',
}

export interface ErpInfo {
  brandId: string;
  erpType: ErpType;
  fileGroup: string;
  warehouseId: string;
}

export interface LocationProperty {
  key: string;
  title: string;
  description: string | null;
}

export interface KeyValue {
  id?: number;
  key: string;
  value: string;
}

export enum Operation {
  useExisting = 'useExisting',
  search = 'search',
  create = 'create',
}

export enum ConfigChoice {
  custom = 'custom',
  default = 'default',
}

export type UserPermissionDTO = {
  permissions: Permission[];
  dataowners: DataownerPermission[];
};

export type UserPermission = {
  brands: Permission[];
  dataowners: DataownerPermission[];
};

export type DataownerPermission = OrgUnitPermission & { locations: OrgUnitPermission[] };

export type OrgUnitPermission = {
  orgType: string;
  orgKey: string;
  permissions: Permission[];
};

export type Permission = {
  resource: Resource;
  actions: Action[];
};

export enum Resource {
  ME = 'ME',
  ADVANCED_SEARCH = 'ADVANCED_SEARCH',

  LOCATION = 'LOCATION',
  LOCATION_GLN = 'LOCATION_GLN',
  LOCATION_PINMETO = 'LOCATION_PINMETO',
  LOCATION_EMPLOYEES = 'LOCATION_EMPLOYEES',
  LOCATION_PROPERTIES = 'LOCATION_PROPERTIES',
  LOCATION_INTEGRATIONS = 'LOCATION_INTEGRATIONS',
  LOCATION_SERVICES = 'LOCATION_SERVICES',
  LOCATION_CHANGELOG = 'LOCATION_CHANGELOG',

  DATAOWNER = 'DATAOWNER',
  DATAOWNER_DETAILS_SERVICES = 'DATAOWNER_DETAILS_SERVICES',
  DATAOWNER_DETAILS_GLN = 'DATAOWNER_DETAILS_GLN',
  DATAOWNER_DETAILS_MEMBERSHIP = 'DATAOWNER_DETAILS_MEMBERSHIP',
  DATAOWNER_LOCATIONS = 'DATAOWNER_LOCATIONS',
  DATAOWNER_EMPLOYEES = 'DATAOWNER_EMPLOYEES',
  DATAOWNER_PROPERTIES = 'DATAOWNER_PROPERTIES',
  DATAOWNER_INTEGRATIONS = 'DATAOWNER_INTEGRATIONS',
  DATAOWNER_CHANGELOG = 'DATAOWNER_CHANGELOG',

  BRAND = 'BRAND',
  BRAND_AUTH0_CLIENT_ID = 'BRAND_AUTH0_CLIENT_ID',
  BRAND_CHANGELOG = 'BRAND_CHANGELOG',
  BRAND_CONFIG_SERVICES = 'BRAND_CONFIG_SERVICES',
  BRAND_RELATIONS = 'BRAND_RELATIONS',
  BRAND_PROPERTIES = 'BRAND_PROPERTIES',
  BRAND_EXT_ID = 'BRAND_EXT_ID',

  ORGANIZATION_OVERVIEW = 'ORGANIZATION_OVERVIEW',
  ORGANIZATION_OVERVIEW_PROPERTIES = 'ORGANIZATION_OVERVIEW_PROPERTIES',

  EMPLOYEE_DETAILS = 'EMPLOYEE_DETAILS',
  EMPLOYEE_LOG = 'EMPLOYEE_LOG',
  EMPLOYEE_USERDATA = 'EMPLOYEE_USERDATA',
  EMPLOYEE_STATUS = 'EMPLOYEE_STATUS',
  EMPLOYEE_ACCOUNT = 'EMPLOYEE_ACCOUNT',
  EMPLOYEE_CONNECTION = 'EMPLOYEE_CONNECTION',
  EMPLOYEE_CHANGELOG = 'EMPLOYEE_CHANGELOG',
}

export enum Action {
  'CREATE' = 'CREATE',
  'READ' = 'READ',
  'UPDATE' = 'UPDATE',
  'DELETE' = 'DELETE',
}
